import React, { type ReactNode, type HTMLAttributes } from 'react';
import { useIsTabletUpV2 } from 'hooks/use-breakpoint-v2';
import { useIsNotMobile } from 'hooks/use-size-class';
import styles from './responsive-container.module.scss';

/**
 * Visibility modes for responsive containers
 * 
 * - 'all': Visible on all screen sizes (default)
 * - 'mobile': Visible only on mobile (< 1024px) - where sticky footer shows
 * - 'tablet-large-up': Visible at tablet-large and up (>= 1024px) - where bento grid shows
 * - 'desktop': Visible only on desktop (>= 1180px)
 * - 'tablet-small-up': Visible only on tablet-small (>= 820px)
 * - 'mobile-small': Visible only on mobile-small (< 820px)
 */
export type ResponsiveVisibility = 'all' | 'mobile' | 'tablet-large-up' | 'desktop' | 'tablet-small-up' | 'mobile-small';

/**
 * Render modes for responsive containers
 * 
 * - 'css': Always render to DOM, use CSS to hide/show (default) - prevents CLS
 * - 'conditional': Conditionally render based on screen size - prevents duplicate semantic HTML (H1, nav, etc.)
 */
export type ResponsiveRenderMode = 'css' | 'conditional';

export interface ResponsiveContainerProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  /** When to show this container */
  show?: ResponsiveVisibility;
  /** Container content */
  children: ReactNode;
  /** HTML element to render (default: div) */
  as?: 'div' | 'section' | 'article' | 'aside' | 'main';
  /** 
   * Render mode: 'css' (default) or 'conditional'
   * 
   * Use 'conditional' when children contain semantic HTML elements (H1, nav, main)
   * that should not be duplicated in the DOM for SEO/accessibility.
   * 
   * Note: 'conditional' mode may cause minor CLS during hydration on slow connections.
   */
  renderMode?: ResponsiveRenderMode;
}

const visibilityStyles: Record<ResponsiveVisibility, string | undefined> = {
  all: undefined,
  mobile: styles.mobile,
  'mobile-small': styles.mobileSmall,
  'tablet-small-up': styles.tabletSmallUp,
  'tablet-large-up': styles.tabletLargeUp,
  desktop: styles.desktop,
};

/**
 * ResponsiveContainer
 * 
 * Renders children with responsive visibility control.
 * 
 * TWO RENDER MODES:
 * -----------------
 * 1. CSS Mode (default): Always renders to DOM, uses CSS media queries to hide/show
 *    - Pros: Zero CLS, instant correct layout before JS loads
 *    - Cons: Duplicate elements in DOM
 *    - Use for: Visual components, images, cards, etc.
 * 
 * 2. Conditional Mode: Only renders when screen size matches
 *    - Pros: No duplicate semantic HTML (H1, nav, main)
 *    - Cons: Minor CLS risk during hydration on slow connections
 *    - Use for: Components with semantic HTML that must be unique in DOM
 * 
 * WHY CSS MODE BY DEFAULT?
 * ------------------------
 * CSS media queries are evaluated immediately by the browser, BEFORE JavaScript
 * loads or React hydrates. This ensures users see the correct layout instantly,
 * even on throttled networks where JS may take seconds to initialize.
 * 
 * Breakpoints (aligned with v2 system):
 * - mobile: < 1024px (sticky footer, carousel gallery)
 * - tablet-large-up: >= 1024px (bento grid, action buttons)
 * - desktop: >= 1180px (full desktop layout with sidebar)
 * 
 * @example
 * // CSS mode (default) - for visual components
 * <ResponsiveContainer show="tablet-large-up">
 *   <DesktopGallery />
 * </ResponsiveContainer>
 * 
 * // Conditional mode - for semantic HTML
 * <ResponsiveContainer show="tablet-large-up" renderMode="conditional">
 *   <DesktopLayout />
 * </ResponsiveContainer>
 */
export function ResponsiveContainer({
  show = 'all',
  children,
  as: Element = 'div',
  className,
  renderMode = 'css',
  ...rest
}: ResponsiveContainerProps) {
  const isTabletUp = useIsTabletUpV2();
  const isDesktop = useIsNotMobile();

  // Conditional rendering mode - only render when screen size matches
  if (renderMode === 'conditional') {
    const shouldRender = 
      show === 'all' ? true :
      show === 'mobile-small' ? !isTabletUp :
      show === 'tablet-small-up' ? isTabletUp :
      show === 'mobile' ? !isDesktop :
      show === 'tablet-large-up' ? isDesktop :
      show === 'desktop' ? isDesktop : // Note: 'desktop' uses same logic as tablet-large-up for now
      true;

    if (!shouldRender) {
      return null;
    }

    // Render without visibility classes when using conditional mode
    return (
      <Element
        data-responsive={show}
        className={className}
        {...rest}
      >
        {children}
      </Element>
    );
  }

  // CSS rendering mode (default) - always render, use CSS to hide/show
  const visibilityClass = visibilityStyles[show];
  const combinedClassName = visibilityClass 
    ? className ? `${visibilityClass} ${className}` : visibilityClass
    : className;

  return (
    <Element
      data-responsive={show}
      className={combinedClassName}
      {...rest}
    >
      {children}
    </Element>
  );
}

export default ResponsiveContainer;
